import dayjs from 'dayjs';
import { isEmpty } from 'lodash-es';
import { FieldValues } from 'react-hook-form';
import {
  ICandidate,
  IDocument,
  IJob,
  INotes,
  submitCandidate,
  updateCandidateSubmission,
} from 'wisestep-api';
import { formattedDateISO } from 'wisestep-base';

import IOtherDocument from '../types/IOtherDocument';
import IResume from '../types/IResume';

// import { settingWillingToTravelType } from './saveMapping';

const getNoticePeriodInDays = (day: { index: number; key: string } | null) => {
  let correctDay = -1;
  if (day) {
    switch (day.key) {
      case 'Immediately':
        correctDay = 0;
        break;
      case '30 Days':
        correctDay = 30;
        break;
      case '45 Days':
        correctDay = 45;
        break;
      case '60 Days':
        correctDay = 60;
        break;
      case '90 Days':
        correctDay = 90;
        break;
    }
  }
  return day && correctDay != -1 ? correctDay : null;
};

const setWorkAuthorization = (authorization, workAuthorization, workAuthorizationCategory) => {
  if (workAuthorizationCategory) {
    if (workAuthorizationCategory === 'Others') {
      return workAuthorization ?? null;
    } else {
      return authorization
        ? typeof authorization == 'object'
          ? authorization.key
          : authorization
        : null;
    }
  }
  return null;
};

export const createSubmissionEntity = (
  data: FieldValues,
  candidate: ICandidate | null,
  job: IJob | null | undefined,
  document?: IOtherDocument[],
  resume?: IResume | null
) => {
  return {
    alternateEmails: !isEmpty(data?.alternateEmails) ? [data.alternateEmails.toString()] : [],
    alternatePhoneNumbers: !isEmpty(data?.alternatePhoneNumbers)
      ? [data?.alternatePhoneNumbers.toString()]
      : [],
    applicationStage: candidate?.applicationStage ?? data?.applicationStage,
    applicationState: candidate?.applicationState ?? data?.applicationState,
    assessment: data.recruiterAssessment,
    candidateId: !isEmpty(data?.candidateId)
      ? data?.candidateId
      : candidate?.id ?? candidate?.candidateId,
    interactionId: candidate?.id,
    candidatePersonalId: data.candidatePersonalId,
    bio: candidate?.bio,
    calendarEvents: null,
    candidateInteractionResult: candidate?.candidateInteractionResult,
    candidateJobBoardId: candidate?.candidateJobBoardId,
    candidateNotes: candidate?.candidateNotes,
    candidateResult: null,
    candidateSkills: candidate?.candidateSkills,
    candidateTasks: candidate?.candidateTasks,
    candidateStatusTags: candidate?.candidateStatusTags,
    certifications: data.certifications ?? candidate?.candidateCertificationsData,
    citizenship: data.citizenship ?? candidate?.citizenship,
    clientJobCode: candidate?.clientJobCode,
    commPreferenceEmail: data.communicationPreference?.key?.includes('Email') ? true : false,
    commPreferencePhone: data.communicationPreference?.key?.includes('Phone') ? true : false,
    jobPreferenceContract: data.jobPreference?.key?.includes('Contract') ? true : false,
    jobPreferencePermanent: data.jobPreference?.key?.includes('Permanent') ? true : false,
    currentEmployer: data.currentCompany,
    currentJobTitle: data.jobTitle,
    currentLocation: data.currentLocation ? data.currentLocation[0] : null,
    currentJobTravelYes: data?.currentJobIncludesTravel?.key === 'Yes' ? true : null,
    currentJobTravelNo: data?.currentJobIncludesTravel?.key === 'No' ? true : null,
    willingToTravel: data?.willingToTravel?.key
      ? data.willingToTravel.key === 'Yes'
        ? true
        : false
      : null,
    willingToTravelNo: data?.willingToTravel?.key === 'Yes' ? true : null,
    willingToTravelYes: data?.willingToTravel?.key === 'Yes' ? true : null,
    willingToTravelType: !isEmpty(data.willingToTravelType) ? data.willingToTravelType : null,
    willingToRelocate: data?.willingToTravel?.key
      ? data.willingToTravel.key === 'Yes'
        ? true
        : false
      : null,
    currentJobType: data?.currentJobType?.key ?? null,
    jobType: null,
    jobWorkAssignment: null,
    companyName: candidate?.companyName,
    consentGiven: null,
    contactActions: candidate?.contactActions,
    contactActionsObjs: candidate?.contactActionsObjs,
    createdBy: null,
    createdOn: null,
    createdWith: 'Wisestep',
    crmState: candidate?.crmState,
    currentSalary: data.currentSalary.value,
    currentSalaryCurrency: data.currentSalary.currency,
    currentSalaryTimeSpan: data.currentSalary.timeSpan,
    customNote: data?.customNote ?? '',
    dateLastActiveOnJobBoard: candidate?.dateLastActiveOnJobBoard,
    dateResumeLastUpdatedOnJobBoard: candidate?.dateResumeLastUpdatedOnJobBoard,
    desiredJobTitles: data?.desiredJobTitles ?? [],
    dnc: candidate?.dnc,
    dncDetails: candidate?.dncDetails,
    dob: data?.dob
      ? dayjs(data?.dob).format('YYYY-MM-DDTHH:mm:ss')
      : candidate?.dob
      ? dayjs(candidate?.dob).format('YYYY-MM-DDTHH:mm:ss')
      : null,
    downloadSource: 'JOBS_SUBMISSION_FORM',
    education: candidate?.education,
    educationQualifications: data.highestEducation,
    email: data.email,
    expectedBaseSalary: data.expectedSalary.value,
    expectedBaseSalaryTimeSpan: data.expectedSalary.timeSpan,
    expectedSalaryCurrency: data.expectedSalary.currency,
    experience: candidate?.experience,
    familyWillingToRelocate:
      data.familyWillingToRelocate && data.familyWillingToRelocate.key == 'Yes' ? true : false,
    relocationPreference: !isEmpty(data.relocationPreferences) ? data.relocationPreferences : null,
    preferredLocations: data.preferredLocation ?? [],
    fax: candidate?.fax,
    gender: data?.gender,
    hasVeteranStatus: data?.hasVeteranStatus ? data?.hasVeteranStatus.key === 'Yes' : null,
    id: null,
    imageUrl: candidate?.imageUrl,
    noticePeriodInDays: getNoticePeriodInDays(data.noticePeriodInDays),
    noticePeriod: null,
    institutionsAttended: data.institutesAttended,
    interests: candidate?.interests,
    isApplied: candidate?.isApplied,
    isCreatedWhenCandidateMapped: true,
    isFromCareerPage: candidate?.isFromCareerPage,
    isInDnc: candidate?.isInDnc,
    isInterested: candidate?.isInterested,
    isJobCandidateMapping: null,
    jobAccountManagerEmail: null,
    jobAccountManagerName: candidate?.jobAccountManagerName,
    jobClientRecruiterEmail: null,
    jobClientRecruiterName: candidate?.jobClientRecruiterName,
    jobCode: null,
    jobId: job?.id ?? candidate?.jobId ?? null,
    jobLocations: null,
    jobMaximumSalary: null,
    jobMinimumSalary: null,
    lastCallAction: candidate?.lastCallAction,
    lastEmailAction: candidate?.lastEmailAction,
    lastSmsAction: candidate?.lastSmsAction,
    likelyToMove: candidate?.likelyToMove,
    likelyToMoveScore: candidate?.likelyToMoveScore,
    linkedCandidateContacts: candidate?.linkedCandidateContacts,
    linkedCandidateIds: candidate?.linkedCandidateIds,
    locations: candidate?.locations,
    mappedJobLocation: candidate?.mappedJobLocation,
    mappedJobTitle: candidate?.mappedJobTitle,
    messengers: data.messengerLinks ?? [],
    modifiedBy: null,
    modifiedOn: null,
    mostRecentConversation: null,
    name: data.name,
    nationality: data.nationality,
    otherCurrentAllowances: data.otherAllowance,
    otherExpectedAllowances: data.expectedAllowance,
    phoneNumber: data?.phone ? data.phone.toString() : '',
    previousEmployers: data.prevEmployers ?? [],
    previousJobTitles: data.prevJobTitles ?? [],
    profileURLs: data.socialLinks ?? [],
    reasonForNewJob: data.jobChangeReason,
    recruiterCompanyName: null,
    recruiterId: null,
    recruiterJobMappingId: candidate?.recruiterJobMappingId,
    recruiterName: null,
    relevantMonthsExperience: data.relExpInMonth,
    relevantYearsExperience: data.relExpInYears,
    responseData: candidate?.responseData,
    rtrConsent: candidate?.rtrConsent,
    rtrFileLocation: candidate?.rtrFileLocation,
    rtrFileOriginalName: candidate?.rtrFileOriginalName,
    rtrFileUploadedOn: candidate?.rtrFileUploadedOn,
    scoringData: candidate?.scoringData,
    searchId: candidate?.searchId,
    securityAllowance: data?.securityClearance
      ? {
          value: null,
          status: data?.securityClearance ? data?.securityClearance.key === 'Yes' : null,
          issuingAgency: null,
          polygraph: null,
          notes: null,
        }
      : candidate?.securityAllowance ?? null,
    sendMessage: candidate?.sendMessage,
    showContact: candidate?.showContact,
    skills: data.skills,
    slug: candidate?.slug,
    socialProfiles: candidate?.socialProfiles,
    socialUrls: candidate?.socialUrls,
    tags: data.tags ?? [],
    source: data.source,
    stageAndStateActions: null,
    stageDate: null,
    submittedOn: null,
    team: candidate?.team,
    timeToPost: null,
    totalMonthsExperience: data.expInMonth,
    totalYearsExperience: data.expInYears,
    turnAroundTime: candidate?.turnAroundTime,
    workAuthorization: data.workAuthorization,
    visaStatusOther: data.visaStatusOther ?? '',
    workAuthorizationCategory: data?.workAuthorizationCategory ?? null,
    // workAuthorization: setWorkAuthorization(
    //   data.authorization,
    //   data?.workAuthorization,
    //   data?.authorizationCategory?.key
    // ),
    // workAuthorizationCategory: data?.authorizationCategory?.key ?? null,
    visaStatus: data.visaStatus,
    visaValidity: data.visaValidity,
    workPermitDocuments: null,
    workPermitLocations: null,
    workPermits: null,
    workPreferences: null,
    workspaceId: null,
    workspaceName: null,
    mappedCandidateResumes: candidate?.mappedCandidateResumes,
    resume: candidate?.mappedCandidateResumes ? candidate?.mappedCandidateResumes[0] : null,
    resumeLocation: resume?.resumeLocation ?? candidate?.resumeLocation,
    resumeFileOriginalName: resume?.resumeFileOriginalName ?? candidate?.resumeFileOriginalName,
    resumeUploadedOn: resume?.resumeUploadedOn
      ? resume?.resumeUploadedOn
      : candidate?.resumeUploadedOn,
    otherDocuments: document ? document : candidate?.otherDocuments,
    jobPostedByRecruiterName: null,
    jobPostingDate: null,
    isSuitable: candidate?.isSuitable,
    jobPostedByTeam: null,
    isMapped: candidate?.isMapped,
    isSubmitted: candidate?.isSubmitted,
    isUploaded: candidate?.isUploaded,
    passportType: data?.passportType,
    passportNumber: data?.passportNumber,
    age: data?.age,
    postalAddress: data?.postalAddress,
    ssnNumber: data?.ssnNumber,
    panCard: data?.panCard,
    aadharNumber: data?.aadharNumber,
    availableFrom: data?.availableFrom,
    interviewingWithOthers: data?.interviewingWithOthers
      ? data.interviewingWithOthers.key === 'Yes'
      : undefined,
    jobIncludesTravel: data?.currentJobIncludesTravel
      ? data.currentJobIncludesTravel.key === 'Yes'
      : undefined,
    currentJobTravelType: data?.currentJobTravelType,
    personalWebsite: data?.personalWebsite ?? '',
    clientBillRate: data?.clientBillRate?.value,
    clientBillRateCurrency: data?.clientBillRate?.currency,
    clientBillRateTimeSpan: data?.clientBillRate?.timeSpan,
    feeArrangementId: job?.feeArrangementId,
    buId: data?.buId?.toString() ?? '',
    offeredPayRate: data?.offeredPayRate?.value,
    offeredPayRateCurrency: data?.offeredPayRate?.currency,
    offeredPayRateTimeSpan: data?.offeredPayRate?.timeSpan,
    questionResponse: data?.questionResponse,
    assignTo: data?.assignedRecruiter ?? '',
    submissionType: data?.submitType ?? '',
    assignToUserName: data?.assignToUserName ?? '',
    skillMatrixRatings: data?.skillMatrixRatings?.filter((item) => item.skill?.trim() !== ''),
    portalId: data?.portalId ?? '',
    exEmployeeOfClient: data?.exEmployeeOfClient?.key
      ? data.exEmployeeOfClient.key === 'Yes'
        ? true
        : false
      : null,
    exEmployeeId: data?.exEmployeeId ?? '',
    workAuthValidity: data?.workAuthValidity ?? null,
    editSubmissionDetails: candidate?.editSubmissionDetails ?? false,
    candidateLanguages: data?.candidateLanguages ?? candidate?.candidateLanguages ?? [],
    otherInterviewStage: data?.otherInterviewStage ?? '',
    linkedInURL: data?.socialLinks[0] ?? '',
    payRateExpenses: data?.payRateExpenses?.value ?? null,
    payRateExpensesCurrency: data?.payRateExpenses?.currency ?? null,
    payRateExpensesType: data?.payRateExpenses.timeSpan ?? candidate?.payRateExpensesType ?? null,
    useRedactedCv: job?.useRedactedCv,
    otherInfo: cleanOtherInfo(data?.otherInfo || []),
    submissionEmailBody: data?.emailBody ?? '',
  };
};
export const saveSubmission = async (
  data: FieldValues,
  candidate: ICandidate | null,
  job: IJob,
  document: IOtherDocument[],
  resume: IResume | null
) => {
  const submissionEntity = createSubmissionEntity(data, candidate, job, document, resume);
  try {
    const submit = await submitCandidate(submissionEntity);
    return submit.data;
  } catch (e) {
    return null;
  }
};

export const saveBulkSubmission = async (
  candidate: ICandidate,
  job: IJob,
  resume?: IResume | null
) => {
  const tempData: ICandidate = candidate;
  tempData.jobId = job.id ?? null;
  tempData.id = null;
  tempData.jobAccountManagerEmail = null;
  tempData.jobAccountManagerName = candidate.jobAccountManagerName;
  tempData.jobClientRecruiterEmail = null;
  tempData.jobClientRecruiterName = candidate.jobClientRecruiterName;
  tempData.jobCode = null;
  tempData.jobWorkAssignment = null;
  tempData.jobType = null;
  tempData.modifiedBy = null;
  tempData.modifiedOn = null;
  tempData.createdOn = null;
  tempData.createdBy = null;
  tempData.workPermitDocuments = null;
  tempData.workPermitLocations = null;
  tempData.workPermits = null;
  tempData.workPreferences = null;
  tempData.workspaceId = null;
  tempData.workspaceName = null;

  if (resume) {
    tempData.resumeLocation = resume?.resumeLocation ?? candidate.resumeLocation;
    tempData.resumeFileOriginalName =
      resume?.resumeFileOriginalName ?? candidate.resumeFileOriginalName;
    tempData.resumeUploadedOn = resume?.resumeUploadedOn
      ? resume?.resumeUploadedOn
      : candidate.resumeUploadedOn;
  }
  try {
    const submit = await submitCandidate(tempData);
    return submit.data;
  } catch (e) {
    return null;
  }
};

export const updateSubmission = async (
  data: FieldValues,
  candidate: ICandidate,
  document: IOtherDocument[],
  resume: IResume | null,
  job: IJob | null | undefined
) => {
  try {
    const submissionEntity = createSubmissionEntity(data, candidate, job, document, resume);
    const submit = await updateCandidateSubmission([submissionEntity]);
    return submit.data;
  } catch (e) {
    return null;
  }
};

export const buildVMSSubmissionEntity = (
  data: FieldValues,
  candidate: ICandidate,
  document: IOtherDocument[],
  resume: IResume | null
) => {
  const submissionEntity = {
    ...candidate,
    alternateEmails: data.alternateEmails,
    alternatePhoneNumbers: !isEmpty(data.alternatePhoneNumbers)
      ? data.alternatePhoneNumbers.map((e) => e.toString())
      : [],
    assessment: data.recruiterAssessment,
    candidateId: data.candidateId,
    candidatePersonalId: data.candidatePersonalId,
    certifications: data.certifications,
    citizenship: data.citizenship,
    commPreferenceEmail: data.communicationPreference?.key?.includes('Email') ? true : false,
    commPreferencePhone: data.communicationPreference?.key?.includes('Phone') ? true : false,
    jobPreferenceContract: data.jobPreference?.key?.includes('Contract') ? true : false,
    jobPreferencePermanent: data.jobPreference?.key?.includes('Permanent') ? true : false,
    currentEmployer: data.currentCompany,
    currentJobTitle: data.jobTitle,
    currentLocation: data.currentLocation ? data.currentLocation[0] : null,
    currentJobTravelYes: data?.currentJobIncludesTravel?.key === 'Yes' ? true : null,
    currentJobTravelNo: data?.currentJobIncludesTravel?.key === 'No' ? true : null,
    willingToTravel: data?.willingToTravel?.key
      ? data.willingToTravel.key === 'Yes'
        ? true
        : false
      : null,
    willingToTravelNo: data?.willingToTravel?.key === 'Yes' ? true : null,
    willingToTravelYes: data?.willingToTravel?.key === 'Yes' ? true : null,
    willingToTravelType: !isEmpty(data.willingToTravelType) ? data.willingToTravelType : null,
    willingToRelocate: data?.willingToTravel?.key
      ? data.willingToTravel.key === 'Yes'
        ? true
        : false
      : null,
    currentJobType: data?.currentJobType?.key ?? null,
    createdWith: 'Wisestep',
    downloadSource: data.downloadSource ?? null,
    currentSalary: data.currentSalary.value,
    currentSalaryCurrency: data.currentSalary.currency,
    currentSalaryTimeSpan: data.currentSalary.timeSpan,
    educationQualifications: data.highestEducation,
    email: data.email,
    expectedBaseSalary: data.expectedSalary.value,
    expectedBaseSalaryTimeSpan: data.expectedSalary.timeSpan,
    expectedSalaryCurrency: data.expectedSalary.currency,
    familyWillingToRelocate:
      data.familyWillingToRelocate && data.familyWillingToRelocate.key == 'Yes' ? true : false,
    relocationPreference: !isEmpty(data.relocationPreferences) ? data.relocationPreferences : null,
    preferredLocations: data.preferredLocations ?? [],
    noticePeriodInDays: getNoticePeriodInDays(data.noticePeriodInDays),
    institutionsAttended: data.institutesAttended,
    isJobCandidateMapping: false,
    name: data.name,
    nationality: data.nationality,
    otherCurrentAllowances: data.otherAllowance,
    otherExpectedAllowances: data.expectedAllowance,
    phoneNumber: data?.phone ? data.phone.toString() : '',
    previousEmployers: data.prevEmployers ?? [],
    previousJobTitles: data.prevJobTitles ?? [],
    profileURLs: data.socialLinks ?? [],
    reasonForNewJob: data.jobChangeReason,
    relevantMonthsExperience: data.relExpInMonth,
    relevantYearsExperience: data.relExpInYears,
    skills: data.skills,
    tags: data.tags ?? [],
    source: data.source,
    totalMonthsExperience: data.expInMonth,
    totalYearsExperience: data.expInYears,
    workAuthorization: setWorkAuthorization(
      data.authorization,
      data?.workAuthorization,
      data?.authorizationCategory?.key
    ),
    workAuthorizationCategory: data?.authorizationCategory?.key ?? null,
    visaStatus: data.visaStatus,
    visaValidity: data.visaValidity,
    resumeLocation: resume?.resumeLocation ?? candidate.resumeLocation,
    resumeFileOriginalName: resume?.resumeFileOriginalName ?? candidate.resumeFileOriginalName,
    resumeUploadedOn: resume?.resumeUploadedOn
      ? resume?.resumeUploadedOn
      : candidate.resumeUploadedOn,
    otherDocuments: document ? document : candidate.otherDocuments,
    isSubmittedToVMS: true,
  };
  return submissionEntity;
};

export const updateResumeAndDocumentsSubmission = async (
  candidate: ICandidate,
  resume: IResume | null,
  document: IDocument[]
) => {
  const submissionEntity = {
    ...candidate,
    mappedCandidateResumes: candidate?.mappedCandidateResumes,
    resumeLocation: resume?.resumeLocation ?? candidate.resumeLocation,
    resumeFileOriginalName: resume?.resumeFileOriginalName ?? candidate.resumeFileOriginalName,
    resumeUploadedOn: resume?.resumeUploadedOn
      ? resume?.resumeUploadedOn
      : candidate.resumeUploadedOn,
    otherDocuments: document ? document : candidate.otherDocuments,
    joiningDate: candidate.joiningDate,
  };
  try {
    const submit = await submitCandidate(submissionEntity);
    return submit.data;
  } catch (e) {
    console.log(e);
  }
};

export const onError = (e: Record<string, any>): string => {
  const errorMessages = Object.values(e)
    .flatMap((error) => {
      if (typeof error === 'object' && error !== null) {
        if (Array.isArray(error)) {
          return error.map((err) => (typeof err === 'object' && err?.message ? err.message : ''));
        }
        return error?.message ? error.message : '';
      }
      return '';
    })
    .filter(Boolean);

  return errorMessages.length > 0 ? errorMessages.join('\n') : '';
};

export const addCandidateNote = (candidate: ICandidate, comment?: string, createdBy = ''): void => {
  if (!comment || isEmpty(comment)) return;

  const note: INotes = {
    notes: comment,
    createdBy,
    createdAt: formattedDateISO(new Date()),
  };

  if (Array.isArray(candidate.candidateNotes)) {
    candidate.candidateNotes.push(note);
  } else {
    candidate.candidateNotes = [note];
  }
};

export const cleanOtherInfo = (list: { key: string; value: string }[] = []) => {
  return list
    .map((item) => ({
      key: item.key?.trim(),
      value: item.value?.trim(),
    }))
    .filter((item) => item.key && item.value);
};
